import { Alert, Form, Input, Radio, Select, Typography } from 'antd';
import type { FormValues, SchemaField, FormSchema, FieldError } from '@/domain/schemaForm';
import { displayLabels, visibleFields } from '@/domain/schemaForm';
import DynamicSelect from './DynamicSelect';

const { Text } = Typography;

/**
 * 按 Airwallex 的动态 schema 渲染表单。**这里没有任何硬编码的业务字段** ——
 * 字段、标签、选项、校验规则全部来自服务端下发的 schema。
 *
 * 之所以要这样：收款人的必填项随国家/币种/汇款方式变化，而且由 Airwallex 跟着各国清算
 * 规则持续更新。硬编码一份表单，就等于承诺自己跟进全球 100+ 个清算系统的字段变更。
 */
export default function SchemaForm({
  schema,
  values,
  errors,
  disabled,
  onChange,
}: {
  schema: FormSchema;
  values: FormValues;
  /** 字段级错误：本地正则校验 + 服务端回传的 Airwallex 出错字段。 */
  errors: Record<string, string>;
  disabled?: boolean;
  /** 值变了就抛给调用方；要不要重新拉 schema 由调用方判断（见 Payees 页）。 */
  onChange: (path: string, value: string) => void;
}) {
  function renderControl(f: SchemaField) {
    const v = values[f.path] ?? '';
    const common = { disabled };

    switch (f.field.type) {
      case 'RADIO':
        return (
          <Radio.Group
            {...common}
            value={v}
            onChange={(e) => onChange(f.path, e.target.value)}
            options={(f.field.options ?? []).map((o) => ({ label: o.label, value: o.value }))}
            optionType="button"
          />
        );

      case 'SELECT':
      case 'TRANSFER_METHOD':
        return (
          <Select
            {...common}
            showSearch
            value={v || undefined}
            placeholder={f.field.placeholder || '请选择'}
            // 国家/币种的候选有两百多项，必须能按拼写过滤
            optionFilterProp="label"
            onChange={(next) => onChange(f.path, next)}
            options={(f.field.options ?? []).map((o) => ({
              label: o.label,
              value: o.value,
              title: o.description,
            }))}
          />
        );

      case 'DYNAMIC_SELECT':
        return (
          <DynamicSelect
            field={f}
            value={v}
            values={values}
            disabled={disabled}
            onChange={(next) => onChange(f.path, next)}
          />
        );

      case 'INPUT':
      default:
        return (
          <Input
            {...common}
            value={v}
            placeholder={f.field.placeholder || f.field.example || ''}
            onChange={(e) => onChange(f.path, e.target.value)}
          />
        );
    }
  }

  const fields = visibleFields(schema);
  const labels = displayLabels(fields);

  return (
    <Form layout="vertical" component="div">
      {fields.map((f) => {
        const err = errors[f.path];
        return (
          <Form.Item
            key={f.path}
            label={labels[f.path]}
            required={f.required}
            validateStatus={err ? 'error' : undefined}
            help={
              err ?? (f.field.description || f.field.tip ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {f.field.description || f.field.tip}
                </Text>
              ) : undefined)
            }
          >
            {renderControl(f)}
          </Form.Item>
        );
      })}

      {fields.length === 0 && (
        <Alert type="info" title="请先选择国家、币种与汇款方式，表单会自动加载。" />
      )}
    </Form>
  );
}

/** 把字段错误数组压成 path → message 的表，供上面的组件消费。 */
export function toErrorMap(list: FieldError[]): Record<string, string> {
  const m: Record<string, string> = {};
  for (const e of list) m[e.path] = e.message;
  return m;
}
